import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { blurbDefault, cap, clean, fieldToken, toParagraph, money, num, shortName, stagger, titleCase, word } from '../utils/quinn';
import { useRawProperty } from '../utils/hooks';
import { PATHS, tourCta, useSiteFlags } from '../utils/site';

interface NextStepProps {
  /** Page aliases for "Keep exploring", in loop order; the first two this property has are shown. */
  explore?: string[];
  /** Buttons: Explore Floor Plans + tour (default), or tour + Apply Now on the floor plans page itself. */
  primary?: 'plans' | 'tour';
  /** Lead default: the homes summary, the tour-then-apply line with the application fee, the layouts to compare, or bedrooms + starting rent + ready now. */
  lead?: 'summary' | 'apply' | 'layouts' | 'browse' | 'homes';
  /** Heading default: the theme line, the schedule-tour blurb's bold sentence ("See X in person"), or "Ready to find your new home?". */
  heading?: 'default' | 'visit' | 'ready';
  /** Background: paper (default) or the surface band (the fee guide). */
  tone?: 'paper' | 'surface';
}

const plansCtaDef: SlotGroupDefinition = {
  label: 'Floor Plans Button',
  fields: {
    label: { label: 'Button Text', type: 'text', defaultValue: 'Explore Floor Plans' },
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const tourCtaDef: SlotGroupDefinition = {
  label: 'Tour Button',
  description: 'Without a self-guided tour URL on the property, the default button becomes Contact Us.',
  fields: {
    label: { label: 'Button Text', type: 'text', defaultValue: 'Book Self-Guided Tour' },
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
    vibesite_event: { label: 'Tracked Event', type: 'event', defaultValue: 'self_guided_tour_clicked' },
  },
};

const applyCtaDef: SlotGroupDefinition = {
  label: 'Apply Now Button',
  description: 'Shown next to the tour button on the floor plans page.',
  fields: {
    label: { label: 'Button Text', type: 'text', defaultValue: 'Apply Now' },
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_apply_now_url',
      suggestedFields: ['property_apply_now_url', 'property_online_leasing_url'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
    vibesite_event: { label: 'Tracked Event', type: 'event', defaultValue: 'apply_now_clicked' },
  },
};

const LOOP: Record<string, [string, string, string]> = {
  gallery: ['gallery', 'Gallery', 'Photos of the homes and the community'],
  'virtual-tours': ['virtualTours', 'Virtual Tours', 'Walk through a home in 3D'],
  neighborhood: ['neighborhood', 'Neighborhood', 'Schools, parks and errands nearby'],
  reviews: ['reviews', 'Reviews', 'What residents say'],
  faqs: ['faqs', 'FAQs', 'Qualifications, pets, and policies'],
  contact: ['contact', 'Contact', 'Talk with the leasing team'],
  'floor-plans': ['floorPlans', 'Floor Plans', 'Every plan with pricing and availability'],
  amenities: ['amenities', 'Amenities', 'Your home and the community'],
};

const HIGHLIGHTS: Array<[string, RegExp]> = [
  ['the pool', /\bpool\b/i],
  ['the dog park', /dog park|dog run|bark park|paw park/i],
  ['the clubhouse', /clubhouse|club house/i],
  ['the fitness center', /fitness|gym/i],
  ['the playground', /playground|tot lot/i],
];

/** "The pool, the dog park and what's in every home": the first two community highlights the property has. */
function amenitiesDesc(p: Record<string, unknown>): string {
  const titles = ((p.amenities as Array<{ title?: string; group?: string }>) || []).filter((a) => a.group !== 'Apartment').map((a) => a.title || '');
  const picks = HIGHLIGHTS.filter(([, rx]) => titles.some((t) => rx.test(t))).map(([w]) => w).slice(0, 2);
  if (!picks.length) return '';
  return cap(`${picks.join(', ')} and what’s in every home`);
}

/** The schema's homes summary needs floor plans or photos; before either exists the lead invites interest instead. */
const COMING_SOON_LEAD = `<p>${fieldToken('property_name')} is coming soon. Join the interest list to hear first about pricing, floor plans and move-in dates.</p>`;

/** "Tour an available home on your own schedule, then apply online. The application fee is $100 per applicant; guarantors are accepted." */
function applyLead(p: Record<string, unknown>, selfTour: boolean, canApply: boolean): string {
  const guarantors = ((p.faqs as Array<{ question?: string }>) || []).some((f) => /guarantor/i.test(f.question || ''));
  let s = selfTour ? 'Tour an available home on your own schedule' : 'See an available home with the leasing team';
  s += canApply ? ', then apply online.' : '.';
  if (num(p.property_application_fee)) {
    s += ` The application fee is $${fieldToken('property_application_fee')} per applicant${guarantors ? '; guarantors are accepted' : ''}.`;
  }
  return `<p>${s}</p>`;
}

/** "Compare the Brunswick, Guilford and Lancaster layouts, then tour an available home on your own schedule." */
function layoutsLead(p: Record<string, unknown>, selfTour: boolean): string {
  const names = ((p.floorplans as Array<{ floorplan_name?: string }>) || []).map((f) => titleCase(clean(f.floorplan_name))).filter(Boolean);
  const tail = selfTour ? 'then tour an available home on your own schedule.' : 'then see one in person with the leasing team.';
  if (names.length < 2 || names.length > 4) return `Compare the floor plans, ${tail}`;
  const list = names.length === 2 ? names.join(' and ') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  return `Compare the ${list} layouts, ${tail}`;
}

/** "Three- and four-bedroom homes from $2,078 a month. Ten are ready to move in now." Clauses without data drop. */
function homesLead(p: Record<string, unknown>): string {
  const [a, b] = String(p.property_bedrooms_range ?? '').split(/\s*-\s*/).map(num);
  const bed = (n: number) => (n === 0 ? 'studio' : `${word(n)}-bedroom`);
  const beds = a == null ? '' : b == null || b === a ? bed(a) : `${a === 0 ? 'studio' : `${word(a)}-`} ${b === a + 1 ? 'and' : 'to'} ${bed(b)}`;
  const price = num(p.property_price_min);
  const ready = num(p.property_available_units);
  const first = beds || price ? cap(`${beds ? `${beds} homes` : 'homes'}${price ? ` from ${money(price)} a month` : ''}.`) : '';
  const second = ready && ready > 0 ? `${cap(word(ready))} ${ready === 1 ? 'is' : 'are'} ready to move in now.` : '';
  return [first, second].filter(Boolean).join(' ');
}

export function NextStep({ explore = ['reviews', 'faqs', 'floor-plans'], primary = 'plans', lead = 'summary', heading = 'default', tone = 'paper' }: NextStepProps) {
  const p = useRawProperty();
  const flags = useSiteFlags();
  const plans = useSlotGroup('nextstep.plans_cta', plansCtaDef);
  const tour = useSlotGroup('nextstep.tour_cta', tourCtaDef);
  const apply = useSlotGroup('nextstep.apply_cta', applyCtaDef);
  const tourLink = tourCta(tour, flags.selfTourUrl);
  const applyHref = apply.url || flags.applyUrl;
  const name = shortName(clean(p.property_name));
  const titleDefault =
    heading === 'visit'
      ? blurbDefault(p.property_scheduletour_blurb, 'title') || toParagraph(name ? `See ${name} in person` : undefined)
      : heading === 'ready'
        ? toParagraph('Ready to find your new home?')
        : undefined;
  const preLeasing = !((p.floorplans as unknown[]) || []).length && !((p.photo_gallery as unknown[]) || []).length;
  const leadDefault =
    lead === 'apply'
      ? applyLead(p, !!flags.selfTourUrl, !!applyHref)
      : lead === 'layouts'
        ? toParagraph(layoutsLead(p, !!flags.selfTourUrl))
        : lead === 'browse'
          ? toParagraph('Explore available homes, compare floor plans, and find the right fit for you.')
          : preLeasing
            ? COMING_SOON_LEAD
            : lead === 'homes'
              ? toParagraph(homesLead(p))
              : undefined;

  const plansButton = (
    <EditableSlotGroup groupPath="nextstep.plans_cta" definition={plansCtaDef}>
      <a className="btn btn-primary" href={plans.url || PATHS.floorPlans} target={plans._target || '_self'}>
        {plans.label || 'Explore Floor Plans'} <span className="btn__arrow">→</span>
      </a>
    </EditableSlotGroup>
  );
  const tourButton = (
    <EditableSlotGroup groupPath="nextstep.tour_cta" definition={tourCtaDef}>
      <a
        className={`btn ${primary === 'tour' ? 'btn-primary' : 'btn-secondary'}`}
        href={tourLink.href}
        target={tourLink.isTour ? tour._target || '_self' : '_self'}
        data-vibesite-event={tourLink.isTour ? tour.vibesite_event || undefined : undefined}
      >
        {tourLink.label}
      </a>
    </EditableSlotGroup>
  );
  const applyButton = (
    <EditableSlotGroup groupPath="nextstep.apply_cta" definition={applyCtaDef}>
      {applyHref && (
        <a className="btn btn-secondary" href={applyHref} target={apply._target || '_self'} data-vibesite-event={apply.vibesite_event || undefined}>
          {apply.label || 'Apply Now'}
        </a>
      )}
    </EditableSlotGroup>
  );

  const keep = explore
    .filter((alias) => LOOP[alias] && flags.shows(alias))
    .slice(0, 2)
    .map((alias) => {
      const [key, name, desc] = LOOP[alias];
      return { href: PATHS[key as keyof typeof PATHS], name, desc: alias === 'amenities' ? amenitiesDesc(p) || desc : desc };
    });

  return (
    <section
      className={`flex items-end justify-between gap-24 px-[--gutter] py-28 max-[1199px]:flex-col max-[1199px]:items-stretch max-[1199px]:gap-12 max-[767px]:gap-6 max-[767px]:py-14${tone === 'surface' ? ' bg-[--q-surface]' : ''}`}
    >
      <div className="flex w-[720px] min-w-0 flex-col gap-6 max-[1199px]:w-auto max-[767px]:gap-4" data-reveal>
        <RichTextContent path="nextstep.eyebrow" as="div" className="eyebrow" />
        <RichTextContent path="nextstep.title" as="h2" className="h1" defaultValue={titleDefault} />
        <RichTextContent
          path="nextstep.lead"
          as="p"
          className="m-0 max-w-[600px] text-lg leading-7 text-[--q-muted] max-[767px]:text-[15px] max-[767px]:leading-[23px]"
          defaultValue={leadDefault}
        />
        <div className="flex gap-3 pt-2 max-[767px]:flex-col max-[767px]:gap-2.5">
          {primary === 'tour' ? (
            <>
              {tourButton}
              {applyButton}
            </>
          ) : (
            <>
              {plansButton}
              {tourButton}
            </>
          )}
        </div>
      </div>
      {keep.length > 0 && (
        <div className="flex w-[360px] shrink-0 flex-col border-t border-[--q-border] max-[1199px]:w-auto" data-reveal style={stagger(1)}>
          <RichTextContent path="nextstep.explore_label" as="div" className="label pb-2.5 pt-4" />
          <div>
            {keep.map((k) => (
              <a
                key={k.href}
                href={k.href}
                className="flex items-center justify-between gap-4 border-t border-[--q-border] py-4 no-underline last:border-b max-[767px]:py-3.5"
              >
                <span className="flex flex-col gap-0.5">
                  <span className="font-heading text-[22px] leading-7 max-[767px]:text-[20px] max-[767px]:leading-[26px]">{k.name}</span>
                  <span className="text-[13px] leading-[18px] text-[--q-muted]">{k.desc}</span>
                </span>
                <span className="btn__arrow text-[20px] text-[--q-primary-on-paper]">→</span>
              </a>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'NextStep',
  displayName: 'Next Step',
  tokens: {
    'nextstep.eyebrow': { label: 'Eyebrow', type: 'richtext', defaultValue: '<p>Next step</p>' },
    'nextstep.title': { label: 'Title', type: 'richtext', defaultValue: '<p>Find the home that fits.</p>' },
    'nextstep.lead': {
      label: 'Lead',
      description:
        'A homes summary from property fields. Some pages swap in their own line (apply steps, layouts to compare), and a property with no floor plans or photos yet says it is coming soon.',
      type: 'richtext',
      defaultValue:
        '<p>Homes with <span data-type="field-token" data-field="property_bedrooms_range"></span> bedrooms and <span data-type="field-token" data-field="property_sqft_range"></span> square feet. <span data-type="field-token" data-field="property_available_units"></span> ready to move in now.</p>',
      generator: {
        prompt: 'A one- or two-sentence summary of the homes here: bedroom counts, the standout home feature, the starting rent, and how many are ready to move in.',
        context: 'Lead of the Next Step band at the bottom of every inner page, above Explore Floor Plans and Book Self-Guided Tour.',
        length: '1–2 sentences',
      },
    },
    'nextstep.explore_label': { label: 'Keep Exploring Label', type: 'richtext', defaultValue: '<p>Keep exploring</p>' },
  },
  slotGroups: {
    'nextstep.plans_cta': plansCtaDef,
    'nextstep.tour_cta': tourCtaDef,
    'nextstep.apply_cta': applyCtaDef,
  },
};

(NextStep as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default NextStep;
